import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Button,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchNodeSchedule } from "../../redux/actions/scheduleActions";
import {
  selectSnapshot,
  selectHasNewData,
  selectLoading,
  selectError,
  acceptLatest,
  NodeSchedule,
  RichSession,
  UnscheduledAPISession,
  APISession,
} from "../../redux/reducers/scheduleReducer";
import { AppDispatch } from "../../redux/store";
import NodeTypeSection from "./schedule/NodeTypeSection";
import DetailPanel from "./schedule/DetailPanel";
import UnscheduledSessionsList from "./schedule/UnscheduledSessionsList";
import NearlySchedulableSessionsList from "./schedule/NearlySchedulableSessionsList";

const POLL_INTERVAL_MS = 10000; // 10 s

const ScheduleInspector: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const snapshot = useSelector(selectSnapshot);
  const hasNewData = useSelector(selectHasNewData);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  const [selectedNode, setSelectedNode] = useState<NodeSchedule | null>(null);
  const [selectedSession, setSelectedSession] = useState<RichSession | null>(
    null
  );
  const [selectedUnscheduledSession, setSelectedUnscheduledSession] = useState<UnscheduledAPISession | null>(
    null
  );
  const [selectedNearlySchedulableSession, setSelectedNearlySchedulableSession] = useState<APISession | null>(
    null
  );
  const [targetsTestInfra, setTargetsTestInfra] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState(0);

  // ———————————————————————— helper
  const refetch = useCallback(
    (flag: boolean) => dispatch(fetchNodeSchedule(flag)),
    [dispatch]
  );

  // first load & polling
  useEffect(() => {
    refetch(targetsTestInfra);
    const id = setInterval(() => {
      refetch(targetsTestInfra);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refetch, targetsTestInfra]);

  // Get unique statuses for filter
  const availableStatuses = React.useMemo(() => {
    const statuses = new Set<string>();
    snapshot?.node_schedules.forEach((n) => {
      statuses.add(n.node_status);
    });
    return Array.from(statuses).sort();
  }, [snapshot]);

  // Filter and group nodes
  const grouped = React.useMemo(() => {
    const map = new Map<string, NodeSchedule[]>();
    snapshot?.node_schedules.forEach((n) => {
      // Apply status filter
      if (statusFilter !== "all" && n.node_status !== statusFilter) {
        return;
      }
      if (!map.has(n.node_type)) map.set(n.node_type, []);
      map.get(n.node_type)!.push(n);
    });
    return map;
  }, [snapshot, statusFilter]);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const flag = e.target.checked;
    setTargetsTestInfra(flag);

    // Close the detail panel when toggling
    setSelectedNode(null);
    setSelectedSession(null);
    setSelectedUnscheduledSession(null);
    setSelectedNearlySchedulableSession(null);

    // fetch → when fulfilled acceptLatest → UI re‑renders immediately
    dispatch(fetchNodeSchedule(flag))
      .unwrap()
      .then(() => dispatch(acceptLatest()))
      .catch(() => toast.error("Failed to load test‑infra schedule"));
  };

  const handleSelectNode = (node: NodeSchedule | null) => {
    setSelectedNode(node);
    setSelectedSession(null);
    setSelectedUnscheduledSession(null);
    setSelectedNearlySchedulableSession(null);
  };

  const handleSelectSession = (session: RichSession | null) => {
    setSelectedSession(session);
    setSelectedUnscheduledSession(null);
    setSelectedNearlySchedulableSession(null);
  };

  const handleSelectUnscheduledSession = (session: UnscheduledAPISession | null) => {
    setSelectedUnscheduledSession(session);
    setSelectedNode(null);
    setSelectedSession(null);
    setSelectedNearlySchedulableSession(null);
  };

  const handleSelectNearlySchedulableSession = (session: APISession | null) => {
    setSelectedNearlySchedulableSession(session);
    setSelectedNode(null);
    setSelectedSession(null);
    setSelectedUnscheduledSession(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Clear selections when switching tabs
    setSelectedNode(null);
    setSelectedSession(null);
    setSelectedUnscheduledSession(null);
    setSelectedNearlySchedulableSession(null);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Professional Top Bar */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            {/* Left Section */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
              sx={{ flex: 1 }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#2d3748",
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                }}
              >
                Schedule Inspector
              </Typography>

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 1,
                  height: 28,
                  display: { xs: "none", sm: "block" },
                }}
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    // Clear all selections when refreshing
                    setSelectedNode(null);
                    setSelectedSession(null);
                    setSelectedUnscheduledSession(null);
                    setSelectedNearlySchedulableSession(null);
                    
                    dispatch(acceptLatest());
                    toast.info("View refreshed with latest snapshot");
                  }}
                  disabled={!snapshot}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#4299e1",
                    "&:hover": { backgroundColor: "#3182ce" },
                    fontSize: "0.875rem",
                    py: 0.5,
                  }}
                >
                  Refresh View
                </Button>

                {hasNewData && (
                  <Chip
                    label="New data available"
                    size="small"
                    color="warning"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      backgroundColor: "#feebc8",
                      color: "#744210",
                    }}
                  />
                )}

                {loading && <CircularProgress size={20} />}
              </Stack>
            </Stack>

            {/* Right Section */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e0",
                    },
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {availableStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={targetsTestInfra}
                    onChange={handleToggle}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#4299e1",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#4299e1",
                        },
                    }}
                  />
                }
                label="Target Test Infra"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color: "#4a5568",
                  },
                }}
              />
            </Stack>
          </Stack>

          {/* Error Alert - Full Width Below */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                py: 0.5,
                backgroundColor: "#fed7d7",
                color: "#742a2a",
                "& .MuiAlert-icon": {
                  color: "#742a2a",
                },
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#4a5568",
              "&.Mui-selected": {
                color: "#4299e1",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#4299e1",
            },
          }}
        >
          <Tab 
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Scheduled Nodes</span>
                {snapshot && (
                  <Chip
                    label={snapshot.node_schedules.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.75rem",
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                    }}
                  />
                )}
              </Stack>
            }
          />
          <Tab 
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Unscheduled Sessions</span>
                {snapshot && snapshot.unscheduled_sessions.length > 0 && (
                  <Chip
                    label={snapshot.unscheduled_sessions.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.75rem",
                      backgroundColor: "#fff3cd",
                      color: "#856404",
                    }}
                  />
                )}
              </Stack>
            }
          />
          <Tab 
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Nearly Schedulable</span>
                {snapshot && snapshot.nearly_schedulable_sessions && snapshot.nearly_schedulable_sessions.length > 0 && (
                  <Chip
                    label={snapshot.nearly_schedulable_sessions.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.75rem",
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                    }}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          overflow: { xs: "auto", lg: "hidden" },
          backgroundColor: "#f7fafc",
        }}
      >
        {/* Tab Content */}
        {activeTab === 0 && (
          /* Node List - Always visible */
          <Box
            sx={{
              flex: {
                xs: "none",
                lg: selectedNode || selectedSession || selectedUnscheduledSession ? "1" : "1",
              },
              width: { xs: "100%", lg: "auto" },
              overflowY: { xs: "visible", lg: "auto" },
              pt: { xs: 2, sm: 3 },
              pr: { lg: selectedNode || selectedSession || selectedUnscheduledSession ? 1 : 0 },
              transition: "flex 0.3s ease",
            }}
          >
            <Stack spacing={2}>
              {Array.from(grouped.entries()).map(([type, nodes]) => (
                <NodeTypeSection
                  key={type}
                  type={type}
                  nodes={nodes}
                  selectedNodeId={selectedNode?.node_id || null}
                  onSelectNode={handleSelectNode}
                  onSelectSession={handleSelectSession}
                  selectedSession={selectedSession}
                  drawerOpen={!!(selectedNode || selectedSession || selectedUnscheduledSession)}
                />
              ))}
            </Stack>
          </Box>
        )}

        {activeTab === 1 && snapshot && (
          /* Unscheduled Sessions List */
          <Box
            sx={{
              flex: {
                xs: "none",
                lg: selectedUnscheduledSession ? "1" : "1",
              },
              width: { xs: "100%", lg: "auto" },
              overflowY: { xs: "visible", lg: "auto" },
              transition: "flex 0.3s ease",
            }}
          >
            <UnscheduledSessionsList
              sessions={snapshot.unscheduled_sessions}
              selectedSessionId={selectedUnscheduledSession?.s.session_id || null}
              onSelectSession={handleSelectUnscheduledSession}
            />
          </Box>
        )}

        {activeTab === 2 && snapshot && (
          /* Nearly Schedulable Sessions List */
          <Box
            sx={{
              flex: {
                xs: "none",
                lg: selectedNearlySchedulableSession ? "1" : "1",
              },
              width: { xs: "100%", lg: "auto" },
              overflowY: { xs: "visible", lg: "auto" },
              transition: "flex 0.3s ease",
            }}
          >
            <NearlySchedulableSessionsList
              sessions={snapshot.nearly_schedulable_sessions || []}
              selectedSessionId={selectedNearlySchedulableSession?.session_id || null}
              onSelectSession={handleSelectNearlySchedulableSession}
            />
          </Box>
        )}

        {/* Detail Panel - Below on mobile, beside on desktop */}
        {(selectedNode || selectedSession || selectedUnscheduledSession || selectedNearlySchedulableSession) && (
          <Box
            sx={{
              flex: { xs: "none", lg: 1 },
              width: { xs: "100%", lg: "auto" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              pt: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                overflow: "hidden",
                height: "100%",
                maxHeight: "80vh",
                boxShadow:
                  "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
              }}
            >
              <DetailPanel
                node={selectedNode}
                session={selectedSession}
                unscheduledSession={selectedUnscheduledSession}
                nearlySchedulableSession={selectedNearlySchedulableSession}
                onCloseNode={() => handleSelectNode(null)}
                onCloseSession={() => handleSelectSession(null)}
                onCloseUnscheduledSession={() => handleSelectUnscheduledSession(null)}
                onCloseNearlySchedulableSession={() => handleSelectNearlySchedulableSession(null)}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScheduleInspector;
